/**
 * ScriptBlocker — Prior-consent script interception.
 *
 * Rewrites non-essential <script> tags to type="text/plain" before they
 * execute, then re-activates them after consent is granted.
 *
 * Must be injected as early as possible in <head>.
 */

export type ConsentCategory = "analytics" | "advertising" | "functional" | "social";

// Known tracking script patterns mapped to consent categories
const SCRIPT_PATTERNS: Array<{ pattern: RegExp; category: ConsentCategory }> = [
  // Analytics
  { pattern: /google-analytics\.com|googletagmanager\.com\/gtag/i, category: "analytics" },
  { pattern: /clarity\.ms/i, category: "analytics" },
  { pattern: /hotjar\.com/i, category: "analytics" },
  { pattern: /mouseflow\.com/i, category: "analytics" },
  { pattern: /fullstory\.com/i, category: "analytics" },
  { pattern: /segment\.com|segment\.io/i, category: "analytics" },
  { pattern: /mixpanel\.com/i, category: "analytics" },
  { pattern: /amplitude\.com/i, category: "analytics" },
  { pattern: /matomo\.|piwik\./i, category: "analytics" },
  // Advertising
  { pattern: /connect\.facebook\.net|fbevents\.js/i, category: "advertising" },
  { pattern: /doubleclick\.net|googlesyndication/i, category: "advertising" },
  { pattern: /ads\.twitter\.com|static\.ads-twitter\.com/i, category: "advertising" },
  { pattern: /snap\.licdn\.com|ads\.linkedin\.com/i, category: "advertising" },
  { pattern: /sc-static\.net|snapchat/i, category: "advertising" },
  { pattern: /bat\.bing\.com/i, category: "advertising" },
  { pattern: /tiktok\.com|analytics\.tiktok/i, category: "advertising" },
  { pattern: /criteo/i, category: "advertising" },
  { pattern: /outbrain\.com/i, category: "advertising" },
  { pattern: /taboola\.com/i, category: "advertising" },
  // Social
  { pattern: /platform\.twitter\.com|twitter\.com\/widgets/i, category: "social" },
  { pattern: /connect\.facebook\.net\/.*\/sdk/i, category: "social" },
  { pattern: /apis\.google\.com\/js\/platform/i, category: "social" },
  { pattern: /static\.addtoany\.com/i, category: "social" },
  // Functional / Chat
  { pattern: /intercom\.io\/widget/i, category: "functional" },
  { pattern: /js\.intercomcdn\.com/i, category: "functional" },
  { pattern: /widget\.drift\.com/i, category: "functional" },
  { pattern: /jivosite\.com/i, category: "functional" },
];

// Known tracking patterns found in inline scripts (textContent matching)
const INLINE_PATTERNS: Array<{ pattern: RegExp; category: ConsentCategory }> = [
  // Google Analytics / GTM inline init
  { pattern: /gtag\s*\(\s*['"]config['"]/i, category: "analytics" },
  { pattern: /GoogleAnalyticsObject/i, category: "analytics" },
  { pattern: /ga\s*\(\s*['"]create['"]/i, category: "analytics" },
  { pattern: /dataLayer\.push\s*\(\s*\{/i, category: "analytics" },
  // Facebook Pixel inline init
  { pattern: /fbq\s*\(\s*['"]init['"]/i, category: "advertising" },
  // LinkedIn Insight
  { pattern: /_linkedin_partner_id/i, category: "advertising" },
  // TikTok Pixel
  { pattern: /ttq\.load\s*\(/i, category: "advertising" },
  // Hotjar
  { pattern: /hj\s*\(\s*['"]init['"]/i, category: "analytics" },
  // Criteo
  { pattern: /criteo_q\.push/i, category: "advertising" },
  // Mixpanel
  { pattern: /mixpanel\.init\s*\(/i, category: "analytics" },
  // Intercom
  { pattern: /Intercom\s*\(\s*['"]boot['"]/i, category: "functional" },
];

/**
 * Inline scripts that belong to the host page's own framework runtime.
 *
 * These carry hydration payloads — React Server Component flight rows, Nuxt /
 * SvelteKit / Remix state — whose serialised page content can contain arbitrary
 * text, including tracker snippets quoted as *data*. Rewriting one to
 * type="text/plain" drops a fragment of the stream, and every framework here
 * concatenates fragments, so the surviving rows no longer parse:
 * "SyntaxError: Expected ':' after property name in JSON".
 *
 * Never block these: they are first-party page infrastructure, not trackers.
 */
const FRAMEWORK_INLINE_PATTERNS: RegExp[] = [
  // Next.js App Router — RSC flight rows: self.__next_f.push([1,"..."])
  /self\s*\.\s*__next_f\b/,
  /\b__next_f\s*(?:\.push\b|=)/,
  // Next.js Pages Router hydration payload + next/script inline queue
  /__NEXT_DATA__/,
  /self\s*\.\s*__next_s\b/,
  /__NEXT_LOADED_PAGES__|__NEXT_P\b/,
  // React 18/19 streaming + Suspense runtime emitted inline during SSR, as
  // either the runtime definition ($RC=function(…)) or a call ($RC("B:0",…)).
  /\$R[BCSTMX]\s*(?:=\s*function\b|\(\s*["'])/,
  /self\s*\.\s*\$R[A-Z]\b/,
  // Bundler runtimes
  /__turbopack|__webpack_require__|webpackChunk|webpackJsonp/,
  // Nuxt / Vue
  /window\s*\.\s*__NUXT__|__unctx__|__NUXT_DATA__/,
  // SvelteKit
  /__sveltekit_/,
  // Remix / React Router
  /__remixContext|__remixRouteModules|__remixManifest/,
  // Gatsby
  /window\s*\.\s*___gatsby|___chunkMapping/,
  // Astro islands
  /astro-island/,
];

/**
 * Script `type` values the browser actually executes. Anything else — JSON
 * payloads, import maps, speculation rules, client-side templates — is inert
 * data that can quote tracker code without running it, so it is never blocked.
 * An empty/absent type means classic JavaScript.
 */
const EXECUTABLE_SCRIPT_TYPES = new Set([
  "",
  "module",
  "text/javascript",
  "application/javascript",
  "text/ecmascript",
  "application/ecmascript",
  "text/jscript",
  "text/babel",
  "module-shim",
]);

/**
 * First-party framework asset paths. A scan-derived pattern (see addPatterns)
 * can be broad enough to match one of these; blocking the app's own runtime
 * chunks would take the page down, so they are excluded unconditionally.
 */
const FRAMEWORK_SRC_PATTERNS: RegExp[] = [
  /\/_next\/(?:static|image|webpack-hmr)\//i,
  /\/__nextjs_/i,
  /\/_nuxt\//i,
  /\/_app\/immutable\//i,
  /\/_astro\//i,
  /\/build\/_shared\//i,
  /\/_vercel\/(?:insights|speed-insights)\//i,
];

export interface BlockPattern {
  pattern: RegExp;
  category: ConsentCategory;
}

export class ScriptBlocker {
  private blocked: Array<{ element: HTMLScriptElement; category: ConsentCategory }> = [];
  private observer: MutationObserver | null = null;
  private restoreDomHooks: (() => void) | null = null;
  // Live consent state so enforcement can continue after the first decision:
  // a tracker injected later in a non-consented category stays blocked.
  private consented: Set<ConsentCategory> = new Set();
  // Instance pattern lists so scan-derived per-domain patterns can be merged in.
  private scriptPatterns: BlockPattern[] = [...SCRIPT_PATTERNS];
  private inlinePatterns: BlockPattern[] = [...INLINE_PATTERNS];

  /**
   * Start blocking non-essential scripts.
   * Call this as early as possible (before DOM is ready).
   */
  intercept(): void {
    this.installDomHooks();

    // Block scripts that are already in the DOM (both external and inline)
    document.querySelectorAll<HTMLScriptElement>("script").forEach((el) => {
      this.maybeBlock(el);
    });

    // Observe future script insertions
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLScriptElement) {
            this.maybeBlock(node);
          }
        });
      }
    });

    this.observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Release scripts for consented categories.
   */
  release(consentedCategories: Set<ConsentCategory>): void {
    // Record the live consent state so future insertions are gated correctly.
    this.consented = new Set(consentedCategories);
    const remaining: typeof this.blocked = [];
    const processed = new Set<HTMLScriptElement>();

    for (const { element, category } of this.blocked) {
      processed.add(element);
      if (consentedCategories.has(category)) {
        this.reactivate(element);
      } else {
        remaining.push({ element, category });
      }
    }

    document
      .querySelectorAll<HTMLScriptElement>('script[type="text/plain"][data-pb-category]')
      .forEach((element) => {
        if (processed.has(element)) return;
        const category = element.dataset["pbCategory"] as ConsentCategory | undefined;
        if (!category || !consentedCategories.has(category)) return;
        this.reactivate(element);
      });

    this.blocked = remaining;
  }

  /**
   * Stop observing the DOM.
   */
  stop(): void {
    this.observer?.disconnect();
    this.observer = null;
    this.restoreDomHooks?.();
    this.restoreDomHooks = null;
  }

  private installDomHooks(): void {
    if (this.restoreDomHooks) return;

    const blocker = this;
    const originalAppendChild = Node.prototype.appendChild;
    const originalInsertBefore = Node.prototype.insertBefore;
    const originalReplaceChild = Node.prototype.replaceChild;

    Node.prototype.appendChild = function<T extends Node>(node: T): T {
      if (node instanceof HTMLScriptElement) blocker.maybeBlock(node);
      return originalAppendChild.call(this, node) as T;
    };

    Node.prototype.insertBefore = function<T extends Node>(node: T, child: Node | null): T {
      if (node instanceof HTMLScriptElement) blocker.maybeBlock(node);
      return originalInsertBefore.call(this, node, child) as T;
    };

    Node.prototype.replaceChild = function<T extends Node>(node: Node, child: T): T {
      if (node instanceof HTMLScriptElement) blocker.maybeBlock(node);
      return originalReplaceChild.call(this, node, child) as T;
    };

    this.restoreDomHooks = () => {
      Node.prototype.appendChild = originalAppendChild;
      Node.prototype.insertBefore = originalInsertBefore;
      Node.prototype.replaceChild = originalReplaceChild;
    };
  }

  /**
   * Merge additional block patterns (a per-domain manifest built from scan
   * results) and re-scan the DOM so trackers the scanner found but the static
   * list misses are also blocked when unconsented.
   */
  addPatterns(patterns: BlockPattern[]): void {
    if (!patterns.length) return;
    this.scriptPatterns.push(...patterns);
    document.querySelectorAll<HTMLScriptElement>("script").forEach((el) => {
      this.maybeBlock(el);
    });
  }

  private maybeBlock(el: HTMLScriptElement): void {
    if (el.dataset["pbReleased"] === "true") return;
    if (el.type === "text/plain") return; // Already blocked
    const category = this.classify(el);
    if (!category) return;
    // Consented categories are never blocked, even for scripts inserted after
    // the first decision, so enforcement can stay live without stopping.
    if (this.consented.has(category)) return;
    this.blockScript(el, category);
  }

  /** Classify a script into a consent category, or null if not one we block. */
  private classify(el: HTMLScriptElement): ConsentCategory | null {
    // An explicit data-pb-category is the site owner declaring what the tag is,
    // so it outranks every heuristic below — including the framework guard. A
    // hand-tagged tracker whose bundled source happens to mention webpackChunk
    // must still be blocked.
    const dataCategory = el.dataset["pbCategory"];
    if (dataCategory) {
      const isBlockable =
        dataCategory === "analytics" ||
        dataCategory === "advertising" ||
        dataCategory === "functional" ||
        dataCategory === "social";
      return isBlockable ? (dataCategory as ConsentCategory) : null;
    }

    // Everything else: the page's own infrastructure is off limits. Touching it
    // breaks the host site rather than a tracker.
    if (ScriptBlocker.isPageInfrastructure(el)) return null;

    const src = el.src || el.getAttribute("src") || "";
    if (src) {
      for (const { pattern, category } of this.scriptPatterns) {
        if (pattern.test(src)) return category;
      }
      return null;
    }
    const content = el.textContent || "";
    if (content.length > 0) {
      for (const { pattern, category } of this.inlinePatterns) {
        if (pattern.test(content)) return category;
      }
    }
    return null;
  }

  /**
   * True when the script is part of the host page itself rather than a
   * third-party tag: a framework hydration payload, a bundler runtime, a
   * first-party framework asset, or an inert non-executable payload.
   *
   * Inline tracker detection matches on textContent, and a framework payload
   * *serialises the whole page* — so a site whose markup merely mentions
   * gtag('config', …) or fbq('init', …) would otherwise have its hydration
   * stream rewritten to text/plain and corrupted.
   *
   * Consulted only for untagged scripts: an explicit data-pb-category is
   * checked first in classify() and always wins. Exported for the regression
   * suite.
   */
  static isPageInfrastructure(el: HTMLScriptElement): boolean {
    const src = el.src || el.getAttribute("src") || "";
    if (src) {
      return FRAMEWORK_SRC_PATTERNS.some((pattern) => pattern.test(src));
    }

    // Inert payloads (application/json, importmap, speculationrules, client
    // templates …) never execute, so they can never be a tracker — but they can
    // quote one.
    const type = (el.getAttribute("type") || "").trim().toLowerCase().split(";")[0]!;
    if (!EXECUTABLE_SCRIPT_TYPES.has(type)) return true;

    const content = el.textContent || "";
    if (!content) return false;
    return FRAMEWORK_INLINE_PATTERNS.some((pattern) => pattern.test(content));
  }

  private blockScript(el: HTMLScriptElement, category: ConsentCategory): void {
    if (el.type === "text/plain") return; // Already blocked
    const originalType = el.getAttribute("type") || "text/javascript";
    el.dataset["pbOriginalType"] = originalType;
    el.type = "text/plain";
    el.dataset["pbCategory"] = category;
    this.blocked.push({ element: el, category });
  }

  private reactivate(el: HTMLScriptElement): void {
    const src = el.getAttribute("src");
    const originalType = el.dataset["pbOriginalType"] || "text/javascript";
    // Must clone and re-insert to trigger execution
    const newScript = document.createElement("script");
    newScript.type = originalType;
    newScript.dataset["pbReleased"] = "true";
    if (src) newScript.src = src;
    else newScript.textContent = el.textContent;

    // Copy attributes
    Array.from(el.attributes).forEach((attr) => {
      if (!["type", "src", "data-pb-category", "data-pb-original-type", "data-pb-released"].includes(attr.name)) {
        newScript.setAttribute(attr.name, attr.value);
      }
    });

    el.parentNode?.replaceChild(newScript, el);
  }
}
